import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

export const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const mismatch = form.confirm && form.confirm !== form.new_password;

  const submit = async (e) => {
    e.preventDefault();
    if (mismatch) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Your password has been updated.");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="change-password-btn"
          className="mt-3 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          <KeyRound className="h-4 w-4" /> Change password
        </button>
      </DialogTrigger>
      <DialogContent data-testid="change-password-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Change your password</DialogTitle>
            <DialogDescription>At least 10 characters with an uppercase letter, a lowercase letter and a number.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" type="password" autoComplete="current-password" required value={form.current_password} onChange={set("current_password")} data-testid="current-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-new">New password</Label>
            <Input id="cp-new" type="password" autoComplete="new-password" required minLength={10} value={form.new_password} onChange={set("new_password")} data-testid="new-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input id="cp-confirm" type="password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} data-testid="confirm-password-input" />
            {mismatch && <p className="text-xs text-destructive" data-testid="password-mismatch">The two passwords do not match.</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="change-password-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving || Boolean(mismatch)} data-testid="change-password-submit-btn">
              {saving ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
